import { useEffect } from "react";
import { useFormik } from "formik";
import { Table, Image } from "antd";
import { useLocation } from "react-router";
import { statusMap } from "../../components/progressBar";
import { useUpdateEffect } from "../../utils/hooksUtil";
import { useDispatch, useSelector } from "react-redux";
import {
  actions,
  articlesInit,
  fetchArticles,
  fetchArticleDetail,
  updateArticle,
} from "../../slices/articlesSlice";
import Header from "../../components/header";
import Footer from "../../components/footer";
import Button from "../../components/button";
import StatusBox from "../../components/statusBox";
import GenernalInput from "../../components/generalInput";
import DeleteModal from "../../components/modal/deleteModal";
import ArticleEditModal from "../../components/modal/articleEditModal";
import ArticleSignUpModal from "../../components/modal/articleSignUp";
import Icon from "@ant-design/icons";
import Icons from "../../constants/Icons";
import TableColumnWidth from "../../constants/tableColumnWidth";
import moment from "moment";
import "./style.scss";

const Articles = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    shops,
    brands,
    articlesList,
    total,
    statusAggregate,
    deleteModalVisible,
    deleteArticle = {},
    searchParams,
  } = useSelector(state => state.articles);

  const formik = useFormik({
    initialValues: searchParams,
    onSubmit: values => dispatch(actions.saveSearchParam(values)),
  });

  useEffect(() => {
    dispatch(articlesInit(location.state));
    return () => dispatch(actions.clear());
  }, [dispatch]);

  useUpdateEffect(
    () => dispatch(fetchArticles(searchParams)),
    [searchParams, dispatch]
  );

  const createOnClikSort = field => () => {
    const desc = `${field}_desc`;
    const asc = `${field}_asc`;
    const targetSort =
      formik.values.sort === asc
        ? desc
        : formik.values.sort === desc
        ? asc
        : asc;
    dispatch(actions.saveSearchParam({ sort: targetSort }));
    formik.setFieldValue("sort", targetSort);
  };
  const columns = [
    {
      title: () => (
        <>
          <span>ブログCD </span>
          <span
            className="sort-span"
            onClick={createOnClikSort("article_code")}
          >
            {formik.values.sort === "article_code_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      dataIndex: "article_code",
      key: "article_code",
      align: "center",
      width: TableColumnWidth.article.cd,
    },
    {
      title: "タイトル画像",
      dataIndex: "title_image",
      key: "title_image",
      render: image =>
        image ? (
          <Image src={image} width={100} height={100} />
        ) : (
          <Icon component={Icons.NoImgaeSquare1} style={{ fontSize: 100 }} />
        ),
      align: "center",
      width: TableColumnWidth.image,
    },
    {
      title: () => (
        <>
          <span>タイトル </span>
          <span
            className="sort-span"
            onClick={createOnClikSort("article_title")}
          >
            {formik.values.sort === "article_title_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      dataIndex: "title",
      key: "title",
      align: "center",
      width: TableColumnWidth.article.title,
      render: title => <div className="article-title-cell">{title || "-"}</div>,
    },
    {
      title: () => (
        <>
          <span>社員 </span>
          <span className="sort-span" onClick={createOnClikSort("user_name")}>
            {formik.values.sort === "user_name_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      key: "user",
      dataIndex: ["user"],
      align: "center",
      width: TableColumnWidth.userName,
      render: user => (
        <span className="display-linebreak">
          {user?.user_name
            ? `${user?.user_name}\n${
                user?.name_nickname ? `(${user?.name_nickname})` : ""
              }`
            : "-"}
        </span>
      ),
    },
    {
      title: () => (
        <>
          <span>店舗 </span>
          <span className="sort-span" onClick={createOnClikSort("shop_name")}>
            {formik.values.sort === "shop_name_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      key: "shop.shop_name",
      dataIndex: ["shop", "shop_name"],
      align: "center",
      width: TableColumnWidth.shopName,
      render: shopName => shopName || "-",
    },
    {
      title: () => (
        <>
          <span>ブランド </span>
          <span className="sort-span" onClick={createOnClikSort("brand_name")}>
            {formik.values.sort === "brand_name_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      key: "brand.brand_name",
      dataIndex: ["brand", "brand_name"],
      align: "center",
      width: TableColumnWidth.brandName,
      render: brandName => brandName || "-",
    },
    {
      title: () => (
        <>
          <span>ステータス </span>
          <span className="sort-span" onClick={createOnClikSort("status")}>
            {formik.values.sort === "status_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      key: "status_flg",
      dataIndex: ["status_flg"],
      align: "center",
      width: TableColumnWidth.status,
      render: status => (
        <span className="display-linebreak">
          {statusMap.find(s => s.value === status)?.status || "-"}
        </span>
      ),
    },
    {
      title: () => (
        <>
          <span>公開日時 </span>
          <span
            className="sort-span"
            onClick={createOnClikSort("publish_datetime")}
          >
            {formik.values.sort === "publish_datetime_asc" ? (
              <Icon
                component={Icons.SortAscendingIcon}
                style={{ fontSize: 10 }}
              />
            ) : (
              <Icon
                component={Icons.SortDescendingIcon}
                style={{ fontSize: 10 }}
              />
            )}
          </span>
        </>
      ),
      key: "published_at",
      dataIndex: "published_at",
      align: "center",
      width: TableColumnWidth.date,
      render: publishAt => (
        <span className="display-linebreak">
          {publishAt
            ? `${moment(publishAt).format("YYYY.MM.DD")}\n${moment(
                publishAt
              ).format("HH:mm")}`
            : "-"}
        </span>
      ),
    },
    {
      title: "編集",
      align: "center",
      //width: TableColumnWidth.icon,
      render: current => (
        <Icon
          component={Icons.EditIcon}
          style={{ fontSize: 25 }}
          onClick={() =>
            dispatch(
              fetchArticleDetail({
                article_code: current.article_code,
                brand_code: current.brand?.brand_code,
                origin_status_flg: current.status_flg,
              })
            )
          }
        />
      ),
    },
    {
      title: "削除",
      align: "center",
      //width: TableColumnWidth.icon,
      render: current => (
        <Icon
          component={Icons.RemoveIcon}
          style={{ fontSize: 25 }}
          onClick={() => dispatch(actions.openDeleteModal(current))}
        />
      ),
    },
  ];
  return (
    <div className="content-body">
      <Header />
      <div className="articles-container nf-page">
        <h2>ブログ一覧</h2>
        <div className="status-box-area">
          {statusMap.map((s, i) => (
            <StatusBox
              status={s.status}
              count={statusAggregate?.[s.value]}
              key={i}
              warning={i < 2}
              onClick={() => dispatch(actions.refineSearch(s.value))}
            />
          ))}
        </div>
        <form className="search-area" onSubmit={formik.handleSubmit}>
          <div className="search-item">
            <GenernalInput
              label="ブログCD"
              placeholder="ブログCDを入力してください"
              formik={formik}
              name={"article_code"}
            />
            <GenernalInput
              label="タイトル"
              placeholder="タイトルを入力してください"
              formik={formik}
              name={"title"}
            />
            <GenernalInput
              label="社員"
              placeholder="社員名またはサイト表示名で入力してください"
              formik={formik}
              name={"user_name"}
            />
            <GenernalInput
              label="店舗"
              mode="multiple"
              name="shop_code"
              placeholder={"店舗を選択してください"}
              formik={formik}
              options={shops}
            />
            <GenernalInput
              label="ブランド"
              mode="multiple"
              name="brand_code"
              placeholder={"ブランドを選択してください"}
              formik={formik}
              options={brands}
            />
          </div>
          <Button text="検索" type="submit" />
        </form>
        <div className="table-header">
          <h3>検索結果一覧</h3>
          <Button
            text="新規作成"
            theme="white"
            width="wider"
            onClick={() => dispatch(actions.openSignUpModal())}
          />
        </div>

        <Table
          columns={columns}
          dataSource={articlesList}
          onChange={pagination => {
            const offset =
              pagination.current === 1
                ? 0
                : pagination.pageSize * (pagination.current - 1);
            dispatch(actions.saveOffset(offset));
            formik.setFieldValue("offset", offset);
          }}
          pagination={{
            total,
            pageSize: 10,
            current: (searchParams?.offset || 0) / 10 + 1,
            position: ["bottomCenter"],
            showQuickJumper: false,
            size: "small",
          }}
          sticky={{ offsetHeader: 60 }}
        />
      </div>
      <ArticleSignUpModal />
      <ArticleEditModal />
      <DeleteModal
        modalVisible={deleteModalVisible}
        onCancel={() => dispatch(actions.closeDeleteModal())}
        item={"ブログCD"}
        itemValue={deleteArticle.article_code}
        onOk={() =>
          dispatch(
            updateArticle({
              division: "delete",
              article_code: deleteArticle.article_code,
              brand_code: deleteArticle?.brand?.brand_code,
              origin_status_flg:
                deleteArticle.status_flg === "4"
                  ? "3"
                  : deleteArticle.status_flg,
            })
          )
        }
      />
      <Footer />
    </div>
  );
};

export default Articles;
